import { observeServerTime } from "../../../../../../shared/frontend/time/serverClock";
import {
HTTP_STATUS_BAD_GATEWAY,
HTTP_STATUS_BAD_REQUEST,
HTTP_STATUS_CONFLICT,
HTTP_STATUS_GATEWAY_TIMEOUT,
HTTP_STATUS_NO_CONTENT,
HTTP_STATUS_SERVICE_UNAVAILABLE,
HTTP_STATUS_UNAUTHORIZED,
HTTP_STATUS_FORBIDDEN,
HTTP_STATUS_INTERNAL_SERVER_ERROR,
HTTP_STATUS_REQUEST_ENTITY_TOO_LARGE,
HTTP_STATUS_REQUEST_TIMEOUT,
HTTP_STATUS_TOO_MANY_REQUESTS
} from "../constants/httpStatus";
import { ApiErrorKeys } from "../constants/apiErrorKeys";
import { PLATFORM_ADMIN_ROLE } from "../constants/authRole";
import { HTTP_METHOD_GET, HTTP_METHOD_HEAD, HTTP_METHOD_OPTIONS } from "./httpMethods";
import { HTTP_REDIRECT_MIN, HTTP_SUCCESS_MIN } from "./httpStatusRanges";
import { signalAppRuntimeUnavailable, trackPendingApiRequest } from "../../../../../../shared/frontend/system/appRuntimeGate";
import { runVersionCheck } from "../versionCheck";
import { loadAuthInfoFromStorage } from "../auth/authStorage";
import type { AuthInfo } from "./types";
import { isTenantImportControlRequest, tenantImportApiScope } from "./tenantImportRequestPolicy";
import { APPLICATION_API_SCOPE, apiRequestAborted, apiRequestSignal, isApiControlRequest, setApiRequestsBlocked, waitForApiRetry } from "../../../../../../shared/frontend/api/apiRequestGate";

const POSSIBLE_PERSON_DUPLICATE_KEY = "PossiblePersonDuplicate";
const MAX_VALIDATION_MESSAGES = 6;
const DEFAULT_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 400;
const RETRY_BACKOFF_FACTOR = 2;
const BACKEND_UNREACHABLE_BASE_WAIT_MS = 2_500;
const BACKEND_UNREACHABLE_MAX_WAIT_MS = 45_000;
const BACKEND_UNREACHABLE_MAX_STEPS = 18;
const PERCENT_MAX = 100;

function tryParseJson(text: string) {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/** Normalisiert die verschiedenen HeadersInit-Formen auf ein einfaches Objekt. */
function toHeaderRecord(init?: HeadersInit): Record<string, string> {
  if (!init) return {};
  if (init instanceof Headers) return Object.fromEntries(init.entries());
  if (Array.isArray(init)) return Object.fromEntries(init);
  return { ...init };
}

/** Liest die Fehlermeldung aus einem unbekannten Fehlerwert. */
function errorMessageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object" && "message" in error) {
    const value = (error as { message?: unknown }).message;
    if (typeof value === "string") return value;
  }
  return "";
}

/**
 * Form eines Fehlerkoerpers, wie ihn ASP.NET Core liefert (ProblemDetails und ModelState).
 * Gross- und Kleinschreibung variieren je nach Serialisierung, deshalb beide Schreibweisen.
 */
type ApiErrorBody = {
  title?: string;
  Title?: string;
  detail?: string;
  Detail?: string;
  message?: string;
  Message?: string;
  requestId?: string;
  traceId?: string;
  errors?: Record<string, unknown>;
  Errors?: Record<string, unknown>;
};

/** Liest das erste vorhandene Feld aus einem unbekannten Fehlerkoerper. */
function readErrorBodyField(body: unknown, ...names: Array<keyof ApiErrorBody>): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  for (const name of names) {
    const value = (body as ApiErrorBody)[name];
    if (typeof value === "string" && value) return value;
  }
  return undefined;
}

const TENANT_IMPORT_COMMIT_BLOCKED_EVENT = "app-tenant-import-commit-blocked";

export function parseErrorKeyFromBodyText(bodyText: string): string | undefined {
  try {
    const j = JSON.parse(bodyText || "{}") as { errorKey?: string; ErrorKey?: string };
    const k = j.errorKey ?? j.ErrorKey;
    return typeof k === "string" ? k : undefined;
  } catch {
    return undefined;
  }
}

function isTenantImportCommitInProgressError(e: ApiError): boolean {
  if (e.status !== HTTP_STATUS_FORBIDDEN) return false;
  return parseErrorKeyFromBodyText(e.bodyText ?? "") === ApiErrorKeys.TenantImportCommitInProgress;
}

export class ApiError extends Error {
  status: number;
  bodyText: string;
  /** RequestId/traceId from backend for support (e.g. 500 responses). */
  requestId?: string;
  /** Request-URL für Logs (welche Anfrage fehlgeschlagen ist). */
  url?: string;

  constructor(message: string, status: number, bodyText: string, requestId?: string, url?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.bodyText = bodyText;
    this.requestId = requestId;
    this.url = url;
  }
}

/** Set in App so API errors can show a global toast. Cleared on unmount. */
let globalApiErrorHandler: ((e: ApiError) => void) | null = null;
export function setGlobalApiErrorHandler(handler: ((e: ApiError) => void) | null) {
  globalApiErrorHandler = handler;
}

export class ApiClient {
  private readonly writeListeners = new Set<(path: string) => void>();
  private readAbort = new AbortController();

  /** Alte API-Sitzungen dürfen weder Leseabrufe noch Retry-Timer zurücklassen. */
  cancelPendingReads() {
    this.readAbort.abort(apiRequestAborted());
    this.readAbort = new AbortController();
  }

  private requestSignal(path: string, opts: RequestInit & { tenantId?: string }): AbortSignal | undefined {
    const method = (opts.method ?? "GET").toUpperCase();
    const read = ["GET", "HEAD", "OPTIONS"].includes(method);
    const signals = opts.signal ? [opts.signal] : [];
    if (read) signals.push(this.readAbort.signal);
    if (!isApiControlRequest(path, method)) {
      const runtimeSignal = apiRequestSignal(APPLICATION_API_SCOPE, method);
      if (runtimeSignal) signals.push(runtimeSignal);
    }
    const info = loadAuthInfoFromStorage();
    const tenantId = opts.tenantId?.trim() || this.getTenantId()?.trim();
    if (tenantId && info?.role !== PLATFORM_ADMIN_ROLE && !isTenantImportControlRequest(path, method)) {
      if (info?.tenantId === tenantId && info.tenantImportCommitInProgress)
        setApiRequestsBlocked(tenantImportApiScope(tenantId), true);
      // Auch neue Schreibaktionen werden vor dem Senden abgelehnt; eine bereits
      // laufende Schreibaktion wird dagegen nie durch diese Lesesperre abgebrochen.
      const importSignal = apiRequestSignal(tenantImportApiScope(tenantId), method);
      if (importSignal) signals.push(importSignal);
    }
    const signal = signals.length ? AbortSignal.any(signals) : undefined;
    signal?.throwIfAborted();
    return signal;
  }

  private handleImportBlock(error: unknown, tenantId?: string) {
    if (!(error instanceof ApiError) || !isTenantImportCommitInProgressError(error)) return;
    const id = tenantId?.trim() || this.getTenantId()?.trim();
    if (id) setApiRequestsBlocked(tenantImportApiScope(id), true);
    window.dispatchEvent(new CustomEvent(TENANT_IMPORT_COMMIT_BLOCKED_EVENT));
  }

  subscribeWrites(listener: (path: string) => void): () => void {
    this.writeListeners.add(listener);
    return () => { this.writeListeners.delete(listener); };
  }
  private getToken: () => string;
  private onUnauthorized?: () => void;

  constructor(opts: { getToken: () => string; onUnauthorized?: () => void }) {
    this.getToken = opts.getToken;
    this.onUnauthorized = opts.onUnauthorized;
  }

  private getTenantId(): string | null {
    try {
      const tenantId = loadAuthInfoFromStorage()?.tenantId;
      if (tenantId) return tenantId;
    } catch {
      // ignore
    }
    // Try to get from URL query parameter
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const tenant = params.get("tenant");
      if (tenant) return tenant;
    }
    return null;
  }

  /**
   * Baut die Kopfzeilen. Rueckgabetyp bewusst `Record<string, string>` und nicht `HeadersInit`:
   * gebaut wird immer ein einfaches Objekt, und der weiter gefasste Typ zwang die Aufrufer
   * frueher zu einem `as any` beim Zusammenfuehren.
   */
  private headers(path: string, extra?: HeadersInit, tenantIdOverride?: string | null): Record<string, string> {
    const h: Record<string, string> = { ...toHeaderRecord(extra) };

    const anonymousAuthPaths = [
      "/api/v1/auth/login",
      "/api/v1/auth/forgot-password",
      "/api/v1/auth/reset-password",
      // Selbstregistrierung: vor der Bestätigung gibt es weder Konto noch Token.
      "/api/v1/auth/register",
      "/api/v1/auth/email-confirm",
      "/api/v1/auth/login",
      "/api/v1/auth/forgot-password",
      "/api/v1/auth/reset-password"
    ];
    if (anonymousAuthPaths.some((p) => path === p || path.startsWith(p + "?"))) return h;
    // Die Buchungswidget-Verwaltung liegt derzeit ebenfalls unter /public/, verlangt
    // aber Bearer-Auth. Ohne Token liefern Schreibaktionen 401 und melden die Sitzung ab.
    const isBookingWidgetAdmin = path.startsWith("/api/v1/public/booking-widgets/admin/");
    // Öffentliche Endpunkte (Tarifkatalog, Zahlungslink …) laufen ohne Anmeldung und Mandant.
    if (path.startsWith("/api/v1/public/") && !isBookingWidgetAdmin) return h;

    const trimmedOverride = tenantIdOverride != null ? String(tenantIdOverride).trim() : "";
    const tenantId = trimmedOverride.length > 0 ? trimmedOverride : this.getTenantId();
    if (tenantId) {
      h["X-Tenant-Id"] = tenantId;
    }
    
    const token = this.getToken();
    if (!token) throw new Error("nicht angemeldet");
    h.Authorization = `Bearer ${token}`;
    return h;
  }

  private async ensureOk(res: Response) {
    if (res.ok) return;
    const txt = await res.text();
    if (res.status === HTTP_STATUS_UNAUTHORIZED) this.onUnauthorized?.();

    const ct = (res.headers.get("content-type") || "").toLowerCase();
    const asJson = ct.includes("application/json") || txt.trim().startsWith("{") ? tryParseJson(txt) : null;
    const requestId = readErrorBodyField(asJson, "requestId", "traceId");

    if (asJson && typeof asJson === "object") {
      const body = asJson as ApiErrorBody;
      const title = body.title ?? body.Title;
      const detail = body.detail ?? body.Detail;
      const messageFromBody = body.message ?? body.Message;
      const errors = body.errors ?? body.Errors;
      if (errors && typeof errors === "object") {
        const msgs: string[] = [];
        for (const k of Object.keys(errors)) {
          const arr = errors[k];
          if (Array.isArray(arr)) arr.forEach((m) => msgs.push(`${k}: ${m}`));
        }
        const msg = msgs.filter(Boolean).slice(0, MAX_VALIDATION_MESSAGES).join("\n") || messageFromBody || title || detail || res.statusText;
        throw new ApiError(String(msg || "Error"), res.status, txt, requestId, res.url);
      }
      const msg = (messageFromBody ?? detail ?? title ?? txt) || res.statusText;
      throw new ApiError(String(msg || "Error"), res.status, txt, requestId, res.url);
    }
    // Proxies/servers often return HTML error pages; do not show them as toasts.
    const isHtml = /^\s*</.test(txt) || txt.includes("<html");
    const fallbackMsg = res.status === HTTP_STATUS_REQUEST_ENTITY_TOO_LARGE ? "Request entity too large" : res.statusText || "Error";
    const msg = isHtml ? fallbackMsg : (txt.trim() || res.statusText);
    throw new ApiError(String(msg || "Error"), res.status, txt, requestId, res.url);
  }

  async req<T>(
    path: string,
    opts: RequestInit & { retries?: number; suppressGlobalErrorToast?: boolean; tenantId?: string } = {}
  ): Promise<T> {
    const signal = this.requestSignal(path, opts);
    const release = trackPendingApiRequest(opts.method ?? "GET");
    try { return await this.executeRequest<T>(path, { ...opts, signal }); }
    finally { release(); }
  }

  private async executeRequest<T>(
    path: string,
    opts: RequestInit & { retries?: number; suppressGlobalErrorToast?: boolean; tenantId?: string }
  ): Promise<T> {
    const retries = opts.retries ?? DEFAULT_RETRIES;
    const suppressGlobalErrorToast = opts.suppressGlobalErrorToast === true;
    const { retries: _retries, suppressGlobalErrorToast: _sup, tenantId: _tenantId, ...fetchOpts } = opts;
    const retryableStatuses = new Set([
      HTTP_STATUS_REQUEST_TIMEOUT,
      HTTP_STATUS_TOO_MANY_REQUESTS,
      HTTP_STATUS_INTERNAL_SERVER_ERROR,
      HTTP_STATUS_BAD_GATEWAY,
      HTTP_STATUS_SERVICE_UNAVAILABLE,
      HTTP_STATUS_GATEWAY_TIMEOUT
    ]);
    /** Während Deploy/Restart kommt nginx mit 502/504 zurück, weil das Backend noch nicht erreichbar ist —
     *  identisch zu 503 (MigrationGate) als Wartung behandeln, statt Fehler-Overlay zu werfen. */
    const backendUnreachableStatuses = new Set<number>([
      HTTP_STATUS_BAD_GATEWAY,
      HTTP_STATUS_SERVICE_UNAVAILABLE,
      HTTP_STATUS_GATEWAY_TIMEOUT
    ]);
    const method = (opts.method ?? "GET").toUpperCase();
    const idempotent = method === HTTP_METHOD_GET || method === HTTP_METHOD_HEAD || method === HTTP_METHOD_OPTIONS;
    /** Nur für 502/503/504-Warteschleife: Backoff, bei Erfolg zurücksetzen (kein hartes Timeout). */
    let backendUnreachableStreak = 0;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        fetchOpts.signal?.throwIfAborted();
        const headers: Record<string, string> = {
          ...this.headers(path, fetchOpts.headers, opts.tenantId)
        };
        const body = fetchOpts.body;
        if (body && !(body instanceof FormData) && !headers["Content-Type"]) headers["Content-Type"] = "application/json";
        // Backend kann bei Retry-Versuchen nur Debug loggen, damit nur ein Log-Eintrag pro Fehler entsteht
        headers["X-Request-Attempt"] = String(attempt);

        const res = await fetch(path, { ...fetchOpts, headers });
        observeServerTime(res.headers);
        runVersionCheck(res.headers.get("X-M1member-Ui-Build"));
        await this.ensureOk(res);
        fetchOpts.signal?.throwIfAborted();
        backendUnreachableStreak = 0;

        if (!idempotent) {
          const pathname = path.split("?")[0];
          // Ein Cache-Listener darf eine bereits erfolgreiche Schreibaktion nicht erneut auslösen.
          for (const listener of this.writeListeners) queueMicrotask(() => listener(pathname));
        }

        if (res.status === HTTP_STATUS_NO_CONTENT) return null as T;

        const ct = (res.headers.get("content-type") || "").toLowerCase();
        const result = ct.includes("application/json") ? await res.json() as T : await res.text() as T;
        fetchOpts.signal?.throwIfAborted();
        return result;
      } catch (e: unknown) {
        if (e instanceof ApiError && isTenantImportCommitInProgressError(e)) {
          this.handleImportBlock(e, opts.tenantId);
          // Kein wartendes Promise und kein eigener Retry pro abgelehnter Anfrage.
          // Der Listener zieht /me nach; nach Abschluss lädt der zentrale Reload frisch.
          throw apiRequestAborted();
        }
        fetchOpts.signal?.throwIfAborted();

        // Backend nicht erreichbar (Deploy/Restart/MigrationGate): 502/503/504 oder reiner Netzwerkfehler.
        // Wartungs-Overlay anzeigen und endlos mit Backoff wiederholen (nur idempotent), damit beim Build
        // kein „Bad Gateway“-Fehler-Overlay erscheint.
        const isBackendUnreachableApiError =
          e instanceof ApiError && backendUnreachableStatuses.has(e.status);
        const isNetworkError =
          !(e instanceof ApiError) &&
          (e instanceof TypeError || /failed to fetch|network|networkerror/i.test(errorMessageOf(e)));
        if (idempotent && (isBackendUnreachableApiError || isNetworkError)) {
          try {
            signalAppRuntimeUnavailable();
          } catch {
            // ignore
          }
          if (!this.getToken()) throw e;
          fetchOpts.signal?.throwIfAborted();
          backendUnreachableStreak++;
          const waitMs = Math.min(BACKEND_UNREACHABLE_MAX_WAIT_MS, BACKEND_UNREACHABLE_BASE_WAIT_MS * Math.min(backendUnreachableStreak, BACKEND_UNREACHABLE_MAX_STEPS));
          await waitForApiRetry(waitMs, fetchOpts.signal);
          attempt--;
          continue;
        }

        // Only retry on retryable status codes (temporary server errors),
        // NOT on business logic errors like 409 (Conflict)
        const isRetryable =
          e instanceof ApiError &&
          retryableStatuses.has(e.status) &&
          !backendUnreachableStatuses.has(e.status) &&
          attempt < retries &&
          idempotent;
        if (isRetryable) {
          const delay = RETRY_BASE_DELAY_MS * Math.pow(RETRY_BACKOFF_FACTOR, attempt);
          await waitForApiRetry(delay, fetchOpts.signal);
          continue;
        }
        // Bei nicht-idempotenten Requests während Deploy/Restart (502/503/504): kein Retry,
        // aber das Wartungs-Overlay zeigen und kein roter Fehler-Toast — die UI erkennt Wartung,
        // statt einen ApiError-Fehler-Dialog zu öffnen.
        if (isBackendUnreachableApiError || isNetworkError) {
          try {
            signalAppRuntimeUnavailable();
          } catch {
            // ignore
          }
          throw e;
        }

        // Toast nur einmal beim endgültigen Fehler (nicht bei jedem Retry-Versuch)
        if (e instanceof ApiError) {
          const isFintsTest400 = e.status === HTTP_STATUS_BAD_REQUEST && path.includes("fints-test");
          const isPossiblePersonDuplicate =
            e.status === HTTP_STATUS_CONFLICT &&
            (parseErrorKeyFromBodyText(e.bodyText ?? "") === POSSIBLE_PERSON_DUPLICATE_KEY ||
              /"message"\s*:\s*"PossiblePersonDuplicate"/i.test(e.bodyText ?? ""));
          if (!suppressGlobalErrorToast && !isFintsTest400 && !isPossiblePersonDuplicate) globalApiErrorHandler?.(e);
        }
        throw e;
      }
    }

    throw new Error("Unerwarteter Fehler");
  }

  async blob(path: string, opts: RequestInit & { tenantId?: string } = {}): Promise<Blob> {
    const signal = this.requestSignal(path, opts);
    const release = trackPendingApiRequest(opts.method ?? "GET");
    try {
    const { tenantId, ...fetchOpts } = opts;
    const headers: Record<string, string> = { ...this.headers(path, fetchOpts.headers, tenantId) };
    const res = await fetch(path, { ...fetchOpts, signal, method: fetchOpts.method ?? "GET", headers });
    observeServerTime(res.headers);
    runVersionCheck(res.headers.get("X-M1member-Ui-Build"));
    await this.ensureOk(res);
    const body = await res.blob();
    signal?.throwIfAborted();
    return body;
    } catch (error) {
      this.handleImportBlock(error, opts.tenantId);
      signal?.throwIfAborted();
      throw error;
    } finally { release(); }
  }

  async postFormWithProgress<T>(
    path: string,
    formData: FormData,
    opts?: {
      timeout?: number;
      signal?: AbortSignal;
      onProgress?: (percent: number) => void;
      onUploadCompleted?: () => void;
      suppressGlobalErrorToast?: boolean;
    }
  ): Promise<T> {
    this.requestSignal(path, { method: "POST", signal: opts?.signal });
    const headers = this.headers(path, {});
    const release = trackPendingApiRequest();

    try { return await new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const cleanup = () => {
        release();
        if (timeoutId != null) clearTimeout(timeoutId);
        if (opts?.signal) opts.signal.removeEventListener("abort", onAbortSignal);
      };

      const fail = (error: unknown) => {
        if (settled) return;
        settled = true;
        cleanup();
        if (error instanceof ApiError && isTenantImportCommitInProgressError(error)) {
          this.handleImportBlock(error);
          reject(apiRequestAborted());
          return;
        }
        const isBackendUnreachable =
          error instanceof ApiError &&
          (error.status === HTTP_STATUS_BAD_GATEWAY ||
            error.status === HTTP_STATUS_SERVICE_UNAVAILABLE ||
            error.status === HTTP_STATUS_GATEWAY_TIMEOUT);
        if (isBackendUnreachable) {
          try {
            signalAppRuntimeUnavailable();
          } catch {
            // ignore
          }
        } else if (error instanceof ApiError) {
          const isFintsTest400 = error.status === HTTP_STATUS_BAD_REQUEST && path.includes("fints-test");
          if (!isFintsTest400 && !opts?.suppressGlobalErrorToast) globalApiErrorHandler?.(error);
        }
        reject(error);
      };

      const succeed = (value: T) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(value);
      };

      const onAbortSignal = () => {
        try {
          xhr.abort();
        } catch {
          // ignore
        }
      };

      xhr.open("POST", path, true);
      if (opts?.timeout != null && opts.timeout > 0) {
        xhr.timeout = opts.timeout;
      }
      for (const [key, value] of Object.entries(headers as Record<string, string>)) {
        if (value != null) xhr.setRequestHeader(key, String(value));
      }

      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        const percent = Math.max(0, Math.min(PERCENT_MAX, Math.round((evt.loaded / evt.total) * PERCENT_MAX)));
        opts?.onProgress?.(percent);
      };

      xhr.upload.onload = () => {
        opts?.onProgress?.(PERCENT_MAX);
        opts?.onUploadCompleted?.();
      };

      xhr.onload = () => {
        runVersionCheck(xhr.getResponseHeader("X-M1member-Ui-Build"));
        const status = xhr.status || 0;
        const txt = xhr.responseText ?? "";
        const ct = (xhr.getResponseHeader("content-type") || "").toLowerCase();
        const asJson = ct.includes("application/json") || txt.trim().startsWith("{") ? tryParseJson(txt) : null;
        const requestId = readErrorBodyField(asJson, "requestId", "traceId");
        if (status >= HTTP_SUCCESS_MIN && status < HTTP_REDIRECT_MIN) {
          if (status === HTTP_STATUS_NO_CONTENT) {
            succeed(null as T);
            return;
          }
          if (ct.includes("application/json")) {
            succeed((asJson as T) ?? (null as T));
            return;
          }
          succeed((txt as unknown) as T);
          return;
        }

        if (status === HTTP_STATUS_UNAUTHORIZED) this.onUnauthorized?.();

        if (asJson && typeof asJson === "object") {
          const body = asJson as ApiErrorBody;
          const title = body.title ?? body.Title;
          const detail = body.detail ?? body.Detail;
          const messageFromBody = body.message ?? body.Message;
          const errors = body.errors ?? body.Errors;
          if (errors && typeof errors === "object") {
            const msgs: string[] = [];
            for (const k of Object.keys(errors)) {
              const arr = errors[k];
              if (Array.isArray(arr)) arr.forEach((m) => msgs.push(`${k}: ${m}`));
            }
            const msg = msgs.filter(Boolean).slice(0, MAX_VALIDATION_MESSAGES).join("\n") || messageFromBody || title || detail || xhr.statusText;
            fail(new ApiError(String(msg || "Fehler"), status, txt, requestId, path));
            return;
          }
          const msg = (messageFromBody ?? detail ?? title ?? txt) || xhr.statusText;
          fail(new ApiError(String(msg || "Error"), status, txt, requestId, path));
          return;
        }

        const isHtml = /^\s*</.test(txt) || txt.includes("<html");
        const fallbackMsg = status === HTTP_STATUS_REQUEST_ENTITY_TOO_LARGE ? "Request entity too large" : xhr.statusText || "Error";
        const msg = isHtml ? fallbackMsg : (txt.trim() || xhr.statusText);
        fail(new ApiError(String(msg || "Error"), status, txt, undefined, path));
      };

      xhr.onerror = () => {
        fail(new ApiError("Network error", xhr.status || 0, xhr.responseText ?? "", undefined, path));
      };

      xhr.onabort = () => {
        fail(new ApiError("Request aborted", xhr.status || 0, xhr.responseText ?? "", undefined, path));
      };

      xhr.ontimeout = () => {
        fail(new ApiError("Timeout", HTTP_STATUS_REQUEST_TIMEOUT, xhr.responseText ?? "", undefined, path));
      };

      if (opts?.signal) {
        if (opts.signal.aborted) {
          onAbortSignal();
          return;
        }
        opts.signal.addEventListener("abort", onAbortSignal, { once: true });
      }

      if (opts?.timeout != null && opts.timeout > 0) {
        timeoutId = setTimeout(() => {
          try {
            xhr.abort();
          } catch {
            // ignore
          }
          fail(new ApiError("Timeout", HTTP_STATUS_REQUEST_TIMEOUT, "", undefined, path));
        }, opts.timeout);
      }

      xhr.send(formData);
    }); } finally { release(); }
  }

  get<T>(
    path: string,
    opts?: { signal?: AbortSignal; tenantId?: string; suppressGlobalErrorToast?: boolean }
  ) {
    return this.req<T>(path, {
      method: "GET",
      signal: opts?.signal,
      tenantId: opts?.tenantId,
      suppressGlobalErrorToast: opts?.suppressGlobalErrorToast
    });
  }
  /** Optional opts.timeout: Request nach ms abbrechen (z. B. für große Uploads). */
  post<T>(
    path: string,
    body?: unknown,
    opts?: { timeout?: number; signal?: AbortSignal; tenantId?: string; suppressGlobalErrorToast?: boolean }
  ) {
    let signal = opts?.signal;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (opts?.timeout != null && opts.timeout > 0 && !signal) {
      const controller = new AbortController();
      signal = controller.signal;
      timeoutId = setTimeout(() => controller.abort(), opts.timeout);
    }
    const reqOpts: RequestInit & { retries?: number; tenantId?: string; suppressGlobalErrorToast?: boolean } = {
      method: "POST",
      body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
      signal,
      tenantId: opts?.tenantId,
      suppressGlobalErrorToast: opts?.suppressGlobalErrorToast === true
    };
    return this.req<T>(path, reqOpts).finally(() => {
      if (timeoutId != null) clearTimeout(timeoutId);
    });
  }
  put<T>(path: string, body?: unknown, opts?: { tenantId?: string; suppressGlobalErrorToast?: boolean }) {
    return this.req<T>(path, {
      method: "PUT",
      body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
      tenantId: opts?.tenantId,
      suppressGlobalErrorToast: opts?.suppressGlobalErrorToast === true
    });
  }
  patch<T>(path: string, body?: unknown, opts?: { tenantId?: string; suppressGlobalErrorToast?: boolean }) {
    return this.req<T>(path, {
      method: "PATCH",
      body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
      tenantId: opts?.tenantId,
      suppressGlobalErrorToast: opts?.suppressGlobalErrorToast === true
    });
  }
  del<T>(path: string, opts?: { tenantId?: string; suppressGlobalErrorToast?: boolean }) {
    return this.req<T>(path, { method: "DELETE", ...opts });
  }

  login(email: string, password: string, stayLoggedIn = true) {
    return this.post<AuthInfo>("/api/v1/auth/login", { email, password, stayLoggedIn });
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.put<void>("/api/v1/auth/me/password", { currentPassword, newPassword });
  }

  forgotPassword(email: string, openInDesktopApp = false, desktopWindowId?: string) {
    return this.post<void>("/api/v1/auth/forgot-password", {
      email, openInDesktopApp, ...(openInDesktopApp && desktopWindowId ? { desktopWindowId } : {}),
    });
  }

  /** Öffentlich: Selbstregistrierung eingeschaltet; ein Mustermandant ist optional. */
  registerTenant(payload: { email: string; password: string; displayName: string; language: string }) {
    return this.post<{ emailConfirmationRequired: boolean }>("/api/v1/auth/register", payload, { suppressGlobalErrorToast: true });
  }

  confirmRegistration(token: string) {
    return this.post<{ success: boolean }>("/api/v1/auth/email-confirm", { token }, { suppressGlobalErrorToast: true });
  }

  resetPassword(token: string, newPassword: string) {
    return this.post<void>("/api/v1/auth/reset-password", { token, newPassword });
  }

  me(signal?: AbortSignal) {
    return this.get<AuthInfo>("/api/v1/auth/me", { signal });
  }

  updateMe(dto: Partial<Pick<AuthInfo, "displayName" | "language" | "calendarDefaultView">>) {
    return this.put<AuthInfo>("/api/v1/auth/me", dto);
  }

  getUiPreferences<T = unknown>() {
    return this.get<T>("/api/v1/auth/ui-preferences");
  }

  putUiPreferences(prefs: unknown) {
    return this.req<void>("/api/v1/auth/ui-preferences", { method: "PUT", body: JSON.stringify(prefs ?? {}) });
  }

  /** Wirksame Design-Vorgaben; ohne Anmeldung nur die App-Vorgaben, angemeldet mit Mandanten-Standard. */
  getThemeAvailability() {
    return this.get<unknown>("/api/v1/settings/theme-availability", { suppressGlobalErrorToast: true });
  }

  /** Standard-Design des Mandanten; `null` = wie App-Standard. Antwort: wirksame Vorgaben. */
  setTenantThemeDefault(themeId: string | null) {
    return this.put<unknown>("/api/v1/settings/theme-default", { themeId }, { suppressGlobalErrorToast: true });
  }

  getAppThemeSettings() {
    return this.get<unknown>("/api/v1/admin/appearance/themes");
  }

  saveAppThemeSettings(settings: unknown) {
    return this.put<unknown>("/api/v1/admin/appearance/themes", settings, { suppressGlobalErrorToast: true });
  }
}
